/**
 * Language, and which way the page runs (plan.md §54, Phase 2).
 *
 * In `shared/` because picking a translation is not a rendering concern: the
 * API needs it for the ICS export and for anything it emails, and a native app
 * will need exactly the same rule. Two implementations of "fall back to the
 * default" is two different sets of half-translated screens.
 *
 * The data model this reads is already everywhere: `name` plus
 * `nameLocal: { he: "…" }`. A base field that is always present and a map of
 * translations that may not be. That shape is what makes the fallback total —
 * there is no state in which a caller gets nothing.
 */

/** BCP-47, but the app only ever ships the ones it has translations for. */
export type Locale = string;

/** The languages MyRave ships. `en` is the base and never a translation. */
export const LOCALES = ['en', 'he'] as const;
export const DEFAULT_LOCALE: Locale = 'en';

/**
 * Scripts written right to left.
 *
 * A list rather than `Intl.Locale.textInfo`, which Safari did not support
 * until recently and still reports inconsistently for regional variants. This
 * is five entries and it is correct everywhere.
 */
const RTL_LANGUAGES = new Set(['he', 'ar', 'fa', 'ur', 'yi']);

export type Direction = 'ltr' | 'rtl';

export function directionOf(locale: Locale): Direction {
  // `he-IL` and `he` are the same question.
  const language = locale.toLowerCase().split(/[-_]/)[0] ?? '';
  return RTL_LANGUAGES.has(language) ? 'rtl' : 'ltr';
}

export function isRtl(locale: Locale): boolean {
  return directionOf(locale) === 'rtl';
}

/**
 * The best available text for a locale.
 *
 * TOTAL by construction: the base value is required, so a missing translation
 * degrades to the original rather than to an empty string. A half-translated
 * lineup showing blank act names is worse than an English one.
 *
 * Falls back through the language before the region — `he-IL` finds `he` —
 * because a festival will translate once and never per country.
 */
export function localized(
  base: string,
  translations: Readonly<Record<string, string>> | undefined,
  locale: Locale,
): string {
  if (!translations) return base;

  const exact = translations[locale];
  if (exact) return exact;

  const language = locale.toLowerCase().split(/[-_]/)[0];
  if (language) {
    const byLanguage = translations[language];
    if (byLanguage) return byLanguage;
  }
  return base;
}

/**
 * Which of the app's languages this visitor should get.
 *
 * Given the browser's ordered preferences, the first one MyRave actually has.
 * Matched by LANGUAGE, not by exact tag: someone whose browser says `he-IL`
 * wants Hebrew, and refusing because the tag is not literally `he` is the kind
 * of correctness nobody thanks you for.
 */
export function negotiateLocale(
  preferred: readonly string[],
  available: readonly Locale[] = LOCALES,
): Locale {
  const availableLanguages = new Map(
    available.map((l) => [l.toLowerCase().split(/[-_]/)[0] ?? l, l]),
  );

  for (const candidate of preferred) {
    const language = candidate.toLowerCase().split(/[-_]/)[0];
    if (!language) continue;
    const hit = availableLanguages.get(language);
    if (hit) return hit;
  }
  return available.includes(DEFAULT_LOCALE) ? DEFAULT_LOCALE : (available[0] ?? DEFAULT_LOCALE);
}

/**
 * Does this edition have anything in that language at all?
 *
 * Used to decide whether to OFFER a language. Showing a Hebrew switch on a
 * festival with no Hebrew content produces a page that is Hebrew in the
 * furniture and English in every word that matters, which reads as broken
 * rather than as untranslated.
 */
export function hasTranslations(
  items: readonly { readonly nameLocal?: Readonly<Record<string, string>> }[],
  locale: Locale,
): boolean {
  const language = locale.toLowerCase().split(/[-_]/)[0] ?? locale;
  return items.some((item) => {
    const local = item.nameLocal;
    return Boolean(local && (local[locale] || local[language]));
  });
}

/* ---- A person's locale: a language AND a region (step 73, 2026-10-22) ----
 *
 * The user, 2026-10-22: "en-US / he-US / en-IL … a world wide app … all
 * languages and all regions … and the user can change it in user settings."
 * (The default they set then — "where the user is, Israel → he-IL" — was
 * replaced on 2026-10-28 by the device's language, below.)
 *
 * So a locale is two independent choices, written as one BCP-47 tag:
 *  - the LANGUAGE the words are in — only one the interface has words for
 *    (`UI_LANGUAGES`); a language without a dictionary would be a page of
 *    English in another language's direction;
 *  - the REGION that formats dates, times, numbers and money — ANY country.
 * "he-US" is Hebrew words with American dates; "en-IL" English words with
 * Israeli dates.
 *
 * Since 2026-10-28 the default is split (the user: "the default language from the system
 * language, and the default country from the location"): the LANGUAGE is the device's
 * (`deviceLocale`), the REGION where the person is (the country Cloudflare sees, `GET /geo`).
 * A person's choice still wins. (From 2026-09-27 to 2026-10-28 the device's language was never
 * used — that rule is gone.)
 */

/**
 * The languages the interface has words in. A language joins only with its
 * dictionary (`frontend/src/i18n/<language>/`, `backend/src/i18n/<language>.ts`,
 * `mobile/locales/`) — the coverage tests hold each to every key.
 *
 * English and Hebrew are written by people; the twenty others (the user,
 * 2026-10-22: "top 20 languages") were drafted by Claude (`pnpm i18n:draft`)
 * for a person to review.
 */
export const UI_LANGUAGES = [
  'en', 'he',
  'es', 'pt', 'de', 'fr', 'it', 'nl', 'ru', 'ja', 'ar', 'fa',
  'tr', 'pl', 'cs', 'hu', 'el', 'uk', 'zh', 'ko', 'hi', 'th',
] as const;
export type UiLanguage = (typeof UI_LANGUAGES)[number];

/** Where nobody knows where the person is. */
export const FALLBACK_LOCALE = 'en-US';

export const isUiLanguage = (l: unknown): l is UiLanguage =>
  typeof l === 'string' && (UI_LANGUAGES as readonly string[]).includes(l);

/** `he-IL` → `he`; `en` → `en`. */
export function languageOf(tag: string): string {
  return tag.toLowerCase().split(/[-_]/)[0] ?? '';
}

/** `he-IL` → `IL`; a tag with no region → null. */
export function regionOf(tag: string): string | null {
  const region = tag.split(/[-_]/).find((part, i) => i > 0 && /^[A-Za-z]{2}$/.test(part));
  return region ? region.toUpperCase() : null;
}

/**
 * The main language of a country, from the platform's own `Intl` (CLDR's
 * likely subtags: IL → he, FR → fr, CH → de) — every country, no list to keep.
 */
export function countryLanguage(country: string): string | null {
  try {
    return new Intl.Locale(`und-${country.toUpperCase()}`).maximize().language || null;
  } catch {
    return null;
  }
}

/**
 * The locale for someone in `country` who has chosen nothing: the country's
 * language when the interface has it (Israel → he-IL), else English, always
 * with the country's own formats (France → en-FR). Unknown → en-US.
 */
export function defaultLocaleFor(country: string | null | undefined): string {
  const region = country && /^[A-Za-z]{2}$/.test(country) ? country.toUpperCase() : null;
  if (!region || region === 'XX' || region === 'T1') return FALLBACK_LOCALE;
  const language = countryLanguage(region);
  return `${isUiLanguage(language) ? language : 'en'}-${region}`;
}

/**
 * The locale for someone who has chosen nothing (the user, 2026-10-28: "the default language
 * from the system language, and the default country from the location … the user can override
 * this and choose a different language"). The LANGUAGE is the device's own — the first of
 * `navigator.languages`, or of an `Accept-Language` header — when the interface has it, else
 * English (the user: "always English", not the country's language). The REGION is where the
 * person is (`country`, Cloudflare's), else the device tag's own region, else the language's
 * main country. An English Mac in Israel → en-IL; a Hebrew one anywhere → he-<there>.
 */
export function deviceLocale(
  languages: readonly string[] | string | null | undefined,
  country: string | null | undefined,
): string {
  const first = firstLanguageTag(languages);
  const spoken = first ? languageOf(first) : '';
  const language = isUiLanguage(spoken) ? spoken : 'en';
  const located = country && /^[A-Za-z]{2}$/.test(country) && !/^(XX|T1)$/i.test(country) ? country.toUpperCase() : null;
  const region = located ?? (first && isUiLanguage(spoken) ? regionOf(first) : null) ?? (language === 'en' ? 'US' : languageRegion(language));
  return `${language}-${region}`;
}

/**
 * The locale of EVERY page for someone who has chosen nothing (the user, 2026-10-30: "the
 * landing pages should use the same language rules: default by device, fallback to location, and
 * fallback to English" — and the app too, "of course"): the app, the landing pages, the legal and
 * install pages, `GET /geo`. The device's language when the interface has it (`deviceLocale`,
 * region from `country`); else the language of the COUNTRY the person is in when the interface
 * has it (a Swedish phone in Israel → he-IL — `defaultLocaleFor`); else English.
 */
export function landingLocale(
  languages: readonly string[] | string | null | undefined,
  country: string | null | undefined,
): string {
  const first = firstLanguageTag(languages);
  if (first && isUiLanguage(languageOf(first))) return deviceLocale(languages, country);
  return defaultLocaleFor(country);
}

/**
 * The device's first language: `navigator.languages[0]`, or the `Accept-Language` entry with the
 * highest weight (`he-IL,he;q=0.9,en;q=0.8` → `he-IL`; ties keep their order). Null when none.
 */
export function firstLanguageTag(languages: readonly string[] | string | null | undefined): string | null {
  if (Array.isArray(languages)) {
    const tag = languages.find((l) => typeof l === 'string' && l.trim() && l.trim() !== '*');
    return tag ? tag.trim() : null;
  }
  if (typeof languages !== 'string') return null;
  let best: { tag: string; q: number } | null = null;
  for (const part of languages.slice(0, 500).split(',')) {
    const [rawTag, ...params] = part.trim().split(';');
    const tag = (rawTag ?? '').trim();
    if (!tag || tag === '*' || !/^[A-Za-z]{1,8}(?:[-_][A-Za-z0-9]{1,8})*$/.test(tag)) continue;
    const qParam = params.map((p) => p.trim()).find((p) => p.startsWith('q='));
    const q = qParam ? Number(qParam.slice(2)) : 1;
    if (!Number.isFinite(q) || q <= 0) continue;
    if (!best || q > best.q) best = { tag, q };
  }
  return best?.tag ?? null;
}

/**
 * A stored or typed tag made safe: a language the interface has, and a
 * two-letter region (the language's own when there is none — `he` → he-IL,
 * `fr` → fr-FR, `en` → en-US). Null for anything else.
 */
export function normalizeLocale(tag: unknown): string | null {
  if (typeof tag !== 'string' || tag.length > 35) return null;
  const language = languageOf(tag);
  if (!isUiLanguage(language)) return null;
  const region = regionOf(tag) ?? languageRegion(language);
  return `${language}-${region}`;
}

/**
 * The region a language is mostly written in (CLDR's likely subtags):
 * he → IL, fr → FR, pt → BR, en → US. What a bare `fr` means.
 */
export function languageRegion(language: string): string {
  try {
    return new Intl.Locale(language).maximize().region ?? 'US';
  } catch {
    return 'US';
  }
}

/* ---- A language's own address, for the public site (step 73.11) ----
 *
 * The user, 2026-10-22: "use the full language and localization, en-us, as I
 * described". An address carries the FULL tag — `?lang=he-IL`, `?lang=en-US` —
 * case-insensitive in, canonical `xx-YY` out. It wins for that visit and is
 * never saved as a choice; search engines get one address per language
 * (`<link rel="alternate" hreflang>`) and `x-default` with no `lang` at all.
 * The tag in an address is only ever the address's own; a page with none follows the device.
 */

/** The query parameter that names a page's locale. */
export const LANG_PARAM = 'lang';

/**
 * A language's main locale: its own country where it has one (`he` → he-IL,
 * `fr` → fr-FR, from CLDR's likely subtags through `Intl`), `en` → en-US.
 */
export function mainLocaleOf(language: string): string {
  const lang = languageOf(language);
  if (lang === 'en') return 'en-US';
  if (lang === 'he') return 'he-IL';
  let region: string | undefined;
  try {
    region = new Intl.Locale(lang).maximize().region;
  } catch {
    region = undefined;
  }
  return `${lang}-${region ?? 'US'}`;
}

/**
 * The locale an address names (`?lang=`), canonical — `HE-il` → `he-IL`, a
 * bare `he` → `he-IL` — or null when it is not a tag of a language the
 * interface has (`fr-FR` today, `he-ZZZ`, `<script>`): such an address is
 * read as if it had no `lang` at all.
 */
export function localeFromParam(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const m = /^([A-Za-z]{2,3})(?:[-_]([A-Za-z]{2}))?$/.exec(value.trim());
  if (!m) return null;
  const language = m[1]!.toLowerCase();
  if (!isUiLanguage(language)) return null;
  return m[2] ? `${language}-${m[2].toUpperCase()}` : mainLocaleOf(language);
}

export interface LanguageAlternate {
  /** `en-US`, `he-IL`, or `x-default`. */
  hreflang: string;
  href: string;
}

/**
 * Every language's address for a public page: one per `UI_LANGUAGES` entry at
 * its main locale, plus `x-default` (the page with no `lang`, which follows
 * the reader's device language and location, 2026-10-28). The page's own other parameters (an invite, a draft)
 * are not part of any of them — one page, one address per language.
 */
export function languageAlternates(pageUrl: string): LanguageAlternate[] {
  const url = new URL(pageUrl);
  const base = `${url.origin}${url.pathname}`;
  return [
    ...UI_LANGUAGES.map((language) => {
      const tag = mainLocaleOf(language);
      return { hreflang: tag, href: `${base}?${LANG_PARAM}=${tag}` };
    }),
    { hreflang: 'x-default', href: base },
  ];
}

/* ---- The first day of the week, from the region (step 73.12) ---- */

/** CLDR's `firstDay` where it is not Monday (supplemental weekData), for an engine without `weekInfo`. */
const SUNDAY_FIRST = new Set([
  'AG', 'AS', 'BD', 'BR', 'BS', 'BT', 'BW', 'BZ', 'CA', 'CN', 'CO', 'DM', 'DO', 'ET', 'GT', 'GU', 'HK', 'HN',
  'ID', 'IL', 'IN', 'JM', 'JP', 'KE', 'KH', 'KR', 'LA', 'MH', 'MM', 'MO', 'MT', 'MX', 'MZ', 'NI', 'NP', 'PA',
  'PE', 'PH', 'PK', 'PR', 'PT', 'PY', 'SA', 'SG', 'SV', 'TH', 'TT', 'TW', 'UM', 'US', 'VE', 'VI', 'WS', 'YE',
  'ZA', 'ZW',
]);
const SATURDAY_FIRST = new Set(['AF', 'BH', 'DJ', 'DZ', 'EG', 'IQ', 'IR', 'JO', 'KW', 'LY', 'OM', 'QA', 'SD', 'SY']);

/** The table's answer for a region (0 = Sunday … 6 = Saturday); Monday when the region is not in it. */
export function firstDayOfWeekForRegion(region: string | null | undefined): number {
  const r = (region ?? '').toUpperCase();
  if (r === 'MV') return 5;
  if (SATURDAY_FIRST.has(r)) return 6;
  if (SUNDAY_FIRST.has(r)) return 0;
  return 1;
}

/**
 * The day a week starts on where this locale's REGION is — 0 = Sunday,
 * 1 = Monday, 6 = Saturday — for every calendar the app draws itself.
 * `he-US` starts on Sunday (the US), `en-GB` on Monday. From the engine's own
 * CLDR (`Intl.Locale#getWeekInfo()`, or the older `weekInfo` getter), else the
 * table above. A tag with no region uses its language's main country.
 */
export function firstDayOfWeek(locale: string): number {
  const region = regionOf(locale) ?? regionOf(mainLocaleOf(locale));
  try {
    const intl = new Intl.Locale(`${languageOf(locale) || 'und'}${region ? `-${region}` : ''}`) as Intl.Locale & {
      getWeekInfo?: () => { firstDay: number };
      weekInfo?: { firstDay: number };
    };
    const info = typeof intl.getWeekInfo === 'function' ? intl.getWeekInfo() : intl.weekInfo;
    const day = info?.firstDay;
    if (typeof day === 'number' && day >= 1 && day <= 7) return day % 7;
  } catch {
    /* an old engine, or a tag it refuses — the table */
  }
  return firstDayOfWeekForRegion(region);
}

/**
 * The languages a rave's OWN words can be translated into (step 73.10): every
 * interface language but the base, English, which is the text itself. A
 * `*Local` map (`{ "he": "…" }`) takes only these keys — the API refuses
 * any other, and the admin's "Add a language" offers exactly these.
 */
export const CONTENT_LANGUAGES: readonly string[] = UI_LANGUAGES.filter((l) => l !== 'en');
